import { useEffect, useState } from "react";
import useLenis from "@/hooks/useLenis";
import { ScrollTrigger } from "@/lib/gsapSetup";
import ErrorBoundary, { SectionFallback } from "@/components/ErrorBoundary";
import Loader from "@/components/Loader";
import Navbar from "@/components/Navbar";
import Hero from "@/components/sections/Hero";
import Clients from "@/components/sections/Clients";
import Services from "@/components/sections/Services";
import WhyChooseUs from "@/components/sections/WhyChooseUs";
import Process from "@/components/sections/Process";
import FeaturedProjects from "@/components/sections/FeaturedProjects";
import Portfolio from "@/components/sections/Portfolio";
import About from "@/components/sections/About";
import Testimonials from "@/components/sections/Testimonials";
import FAQ from "@/components/sections/FAQ";
import ContactCTA from "@/components/sections/ContactCTA";
import Footer from "@/components/sections/Footer";

function SectionBoundary({ children }) {
  return (
    <ErrorBoundary fallback={({ retry }) => <SectionFallback retry={retry} />}>
      {children}
    </ErrorBoundary>
  );
}

export default function Landing() {
  const skipLoader =
    typeof window !== "undefined" &&
    (window.location.search.includes("preview") ||
      window.sessionStorage.getItem("aurelia_seen") === "1");
  const [loaded, setLoaded] = useState(skipLoader);
  useLenis();

  const handleLoaded = () => {
    try {
      window.sessionStorage.setItem("aurelia_seen", "1");
    } catch (e) {
      /* storage may be unavailable — non-critical */
    }
    setLoaded(true);
  };

  // Keep ScrollTrigger positions accurate across fonts/images/layout/media shifts
  useEffect(() => {
    const refresh = () => ScrollTrigger.refresh();
    const timers = [setTimeout(refresh, 300), setTimeout(refresh, 1200)];

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(refresh).catch(() => {});
    }
    window.addEventListener("load", refresh);

    // refresh as each not-yet-loaded image settles
    const imgs = Array.from(document.images);
    const onImg = () => refresh();
    imgs.forEach((img) => {
      if (!img.complete) img.addEventListener("load", onImg, { once: true });
    });

    // refresh on viewport resize (layout shifts / orientation)
    let resizeTO;
    const onResize = () => {
      clearTimeout(resizeTO);
      resizeTO = setTimeout(refresh, 200);
    };
    window.addEventListener("resize", onResize);

    return () => {
      timers.forEach(clearTimeout);
      clearTimeout(resizeTO);
      window.removeEventListener("load", refresh);
      window.removeEventListener("resize", onResize);
    };
  }, [loaded]);

  return (
    <div className="relative min-h-screen bg-[#050505] text-white antialiased">
      {!skipLoader && <Loader onComplete={handleLoaded} />}
      <SectionBoundary>
        <Navbar ready={loaded} />
      </SectionBoundary>

      <main>
        <SectionBoundary>
          <Hero ready={loaded} />
        </SectionBoundary>
        <SectionBoundary>
          <Clients />
        </SectionBoundary>
        <SectionBoundary>
          <Services />
        </SectionBoundary>
        <SectionBoundary>
          <WhyChooseUs />
        </SectionBoundary>
        <SectionBoundary>
          <Process />
        </SectionBoundary>
        <SectionBoundary>
          <FeaturedProjects />
        </SectionBoundary>
        <SectionBoundary>
          <Portfolio />
        </SectionBoundary>
        <SectionBoundary>
          <About />
        </SectionBoundary>
        <SectionBoundary>
          <Testimonials />
        </SectionBoundary>
        <SectionBoundary>
          <FAQ />
        </SectionBoundary>
        <SectionBoundary>
          <ContactCTA />
        </SectionBoundary>
      </main>

      <SectionBoundary>
        <Footer />
      </SectionBoundary>
    </div>
  );
}
